import { ApiError, apiRequest, fetchAuthenticatedBlob } from './client';
import {
  hasVideoExtension,
  validateRecommendationLetterImage,
  validateToothSpacingMapImage,
  validateVisualizationPhoto,
  validateVisualizationVideo,
} from '../utils/fileValidation';
import type {
  CycleLabelKind,
  PaginatedResponse,
  Patient,
  PatientCreatedResponse,
  Treatment,
  TreatmentStage,
  TreatmentStageStep,
  TreatmentStatus,
  StageKind,
  StageStatus,
} from '../types/admin';

export type AccountState = 'pending' | 'active' | 'inactive';

export type ListPatientsParams = {
  readonly page?: number;
  readonly page_size?: number;
  readonly search?: string;
  readonly treatment_status?: TreatmentStatus | null;
  readonly mine?: boolean;
  readonly assigned_admin_ids?: readonly string[];
  readonly current_stage_kind?: StageKind | null;
  readonly current_cycle_number?: number | null;
  readonly account_state?: AccountState | null;
  readonly email_contains?: string;
  readonly updated_from?: string;
  readonly updated_to?: string;
};

export type PatientAssignmentSummary = {
  readonly admin_id: string;
  readonly user_id: string;
  readonly display_name: string;
};

export type CreatePatientPayload = {
  readonly first_name: string;
  readonly last_name: string;
};

export type UpdatePatientPayload = {
  readonly lead_id?: string | null;
};

export type CreateTreatmentPayload = {
  readonly status?: TreatmentStatus;
  readonly diagnosis_title?: string | null;
  readonly diagnosis_titles?: readonly string[] | null;
  readonly patient_features?: readonly string[] | null;
};

export type UpdateTreatmentPayload = Partial<CreateTreatmentPayload>;

export type CreateTreatmentStagePayload = {
  readonly cycle_number?: number;
  readonly upper_aligners_count?: number | null;
  readonly lower_aligners_count?: number | null;
  readonly current_stage_kind?: StageKind | null;
  readonly patient_stage_title?: string | null;
  readonly cycle_label_kind?: CycleLabelKind;
};

export type UpdateTreatmentStagePayload = Omit<CreateTreatmentStagePayload, 'cycle_number'> & {
  readonly visualization_title?: string | null;
  readonly visualization_description?: string | null;
  readonly treatment_description?: string | null;
  readonly estimated_treatment_days?: number | null;
  readonly aligner_unit_price?: string | null;
  readonly prepayment_amount?: string | null;
  readonly discount_amount?: string | null;
  readonly additional_items?: readonly { name: string; price: string }[] | null;
  readonly pricing_currency?: string | null;
};

export type UpdateTreatmentStageStepPayload = {
  readonly status: StageStatus;
};

export type CreateTreatmentCyclePayload = CreateTreatmentStagePayload;
export type UpdateTreatmentCyclePayload = UpdateTreatmentStagePayload;
export type UpdateTreatmentCycleStagePayload = UpdateTreatmentStageStepPayload;

export function normalizeTreatmentStage(stage: TreatmentStage): TreatmentStage {
  return {
    ...stage,
    status: stage.status ?? 'in_progress',
    steps: stage.stages,
    procedures: stage.procedures ?? [],
    // Older backends (and the cached payloads they produced) omit these
    // entirely; defaulting here keeps every consumer free of null checks.
    plan_options: stage.plan_options ?? [],
    movement_steps: stage.movement_steps ?? [],
    selected_plan_option_id: stage.selected_plan_option_id ?? null,
  };
}

function normalizeTreatment(treatment: Treatment): Treatment {
  return {
    ...treatment,
    stages: treatment.cycles.map(normalizeTreatmentStage),
  };
}

export async function getPatients(
  params: ListPatientsParams = {},
  accessToken: string,
): Promise<PaginatedResponse<Patient>> {
  const {
    page = 1,
    page_size = 10,
    search,
    treatment_status,
    mine,
    assigned_admin_ids,
    current_stage_kind,
    current_cycle_number,
    account_state,
    email_contains,
    updated_from,
    updated_to,
  } = params;
  const query = new URLSearchParams({
    page: String(page),
    page_size: String(page_size),
  });

  if (search) {
    query.set('search', search);
  }

  if (treatment_status) {
    query.set('treatment_status', treatment_status);
  }

  if (mine !== undefined) {
    query.set('mine', mine ? 'true' : 'false');
  }

  if (assigned_admin_ids && assigned_admin_ids.length > 0) {
    for (const adminId of assigned_admin_ids) {
      query.append('assigned_admin_ids', adminId);
    }
  }

  if (current_stage_kind) {
    query.set('current_stage_kind', current_stage_kind);
  }

  if (current_cycle_number !== undefined && current_cycle_number !== null) {
    query.set('current_cycle_number', String(current_cycle_number));
  }

  if (account_state) {
    query.set('account_state', account_state);
  }

  if (email_contains) {
    query.set('email_contains', email_contains);
  }

  if (updated_from) {
    query.set('updated_from', updated_from);
  }

  if (updated_to) {
    query.set('updated_to', updated_to);
  }

  return apiRequest<PaginatedResponse<Patient>>(`/admin/patients?${query.toString()}`, {
    token: accessToken,
  });
}

export function getPatientAssignments(
  patientId: string,
  accessToken: string,
): Promise<readonly PatientAssignmentSummary[]> {
  return apiRequest<readonly PatientAssignmentSummary[]>(
    `/admin/patients/${patientId}/assignments`,
    { token: accessToken },
  );
}

export function putPatientAssignments(
  patientId: string,
  adminIds: readonly string[],
  accessToken: string,
): Promise<readonly PatientAssignmentSummary[]> {
  return apiRequest<readonly PatientAssignmentSummary[]>(
    `/admin/patients/${patientId}/assignments`,
    {
      method: 'PUT',
      body: { admin_ids: [...adminIds] },
      token: accessToken,
    },
  );
}

export async function getPatient(id: string, accessToken: string): Promise<Patient | null> {
  try {
    return await apiRequest<Patient>(`/admin/patients/${id}`, {
      token: accessToken,
    });
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export function getPatientTreatments(
  patientId: string,
  accessToken: string,
): Promise<readonly Treatment[]> {
  return apiRequest<readonly Treatment[]>(`/admin/patients/${patientId}/treatments`, {
    token: accessToken,
  }).then((treatments) => treatments.map(normalizeTreatment));
}

export function createPatient(payload: CreatePatientPayload, accessToken: string): Promise<PatientCreatedResponse> {
  return apiRequest<PatientCreatedResponse>('/admin/patients', {
    method: 'POST',
    body: payload,
    token: accessToken,
  });
}

export function updatePatient(
  patientId: string,
  payload: UpdatePatientPayload,
  accessToken: string,
): Promise<Patient> {
  return apiRequest<Patient>(`/admin/patients/${patientId}`, {
    method: 'PATCH',
    body: payload,
    token: accessToken,
  });
}

export function createPatientTreatment(
  patientId: string,
  payload: CreateTreatmentPayload,
  accessToken: string,
): Promise<Treatment> {
  return apiRequest<Treatment>(`/admin/patients/${patientId}/treatments`, {
    method: 'POST',
    body: payload,
    token: accessToken,
  }).then(normalizeTreatment);
}

export function updatePatientTreatment(
  patientId: string,
  treatmentId: string,
  payload: UpdateTreatmentPayload,
  accessToken: string,
): Promise<Treatment> {
  return apiRequest<Treatment>(`/admin/patients/${patientId}/treatments/${treatmentId}`, {
    method: 'PATCH',
    body: payload,
    token: accessToken,
  }).then(normalizeTreatment);
}

export function deletePatientTreatment(
  patientId: string,
  treatmentId: string,
  accessToken: string,
): Promise<void> {
  return apiRequest<void>(`/admin/patients/${patientId}/treatments/${treatmentId}`, {
    method: 'DELETE',
    token: accessToken,
  });
}

export function deletePatient(patientId: string, accessToken: string): Promise<void> {
  return apiRequest<void>(`/admin/patients/${patientId}`, {
    method: 'DELETE',
    token: accessToken,
  });
}

export function createTreatmentStage(
  patientId: string,
  treatmentId: string,
  payload: CreateTreatmentStagePayload,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(`/admin/patients/${patientId}/treatments/${treatmentId}/cycles`, {
    method: 'POST',
    body: payload,
    token: accessToken,
  }).then(normalizeTreatmentStage);
}

export function updateTreatmentStage(
  patientId: string,
  treatmentId: string,
  stageId: string,
  payload: UpdateTreatmentStagePayload,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(`/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${stageId}`, {
    method: 'PATCH',
    body: payload,
    token: accessToken,
  }).then(normalizeTreatmentStage);
}

export function deleteTreatmentStage(
  patientId: string,
  treatmentId: string,
  stageId: string,
  accessToken: string,
): Promise<void> {
  return apiRequest<void>(
    `/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${stageId}`,
    {
      method: 'DELETE',
      token: accessToken,
    },
  );
}

export function updateCycleStatus(
  patientId: string,
  treatmentId: string,
  cycleId: string,
  status: 'draft' | 'in_progress' | 'completed' | 'cancelled',
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(
    `/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${cycleId}/status`,
    {
      method: 'PATCH',
      body: { status },
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function uploadTreatmentStageVisualizationMedia(
  patientId: string,
  treatmentId: string,
  stageId: string,
  file: File,
  accessToken: string,
): Promise<TreatmentStage> {
  const isVideo =
    file.type.startsWith('video/') ||
    ((!file.type || file.type === 'application/octet-stream') && hasVideoExtension(file.name));
  const isImage = file.type.startsWith('image/');

  if (isVideo) {
    const videoError = validateVisualizationVideo(file);
    if (videoError === 'type') {
      return Promise.reject(
        new ApiError('Unsupported video type. Please upload MP4, MOV, WebM, or AVI.', 415),
      );
    }
    if (videoError === 'size') {
      return Promise.reject(new ApiError('Video file exceeds the 20 MB size limit.', 413));
    }
  } else if (isImage) {
    const photoError = validateVisualizationPhoto(file);
    if (photoError === 'type') {
      return Promise.reject(
        new ApiError('Unsupported photo type. Please upload JPEG, PNG, or WebP.', 415),
      );
    }
    if (photoError === 'size') {
      return Promise.reject(new ApiError('Photo file exceeds the 10 MB size limit.', 413));
    }
  } else {
    return Promise.reject(new ApiError('Unsupported file type for visualization media.', 415));
  }

  const body = new FormData();
  body.append('file', file);
  return apiRequest<TreatmentStage>(
    `/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${stageId}/visualization-media`,
    {
      method: 'POST',
      body,
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function deleteTreatmentStageVisualizationMedia(
  patientId: string,
  treatmentId: string,
  stageId: string,
  mediaId: string,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(
    `/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${stageId}/visualization-media/${mediaId}`,
    {
      method: 'DELETE',
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function reorderTreatmentStageVisualizationMedia(
  patientId: string,
  treatmentId: string,
  stageId: string,
  orderedIds: readonly string[],
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(
    `/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${stageId}/visualization-media/reorder`,
    {
      method: 'POST',
      body: { ordered_ids: [...orderedIds] },
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function uploadTreatmentRecommendationLetter(
  patientId: string,
  treatmentId: string,
  file: File,
  accessToken: string,
): Promise<Treatment> {
  const validationError = validateRecommendationLetterImage(file);
  if (validationError === 'type') {
    return Promise.reject(
      new ApiError('Unsupported image type. Please upload JPEG, PNG, or WebP.', 415),
    );
  }
  if (validationError === 'size') {
    return Promise.reject(new ApiError('Image file exceeds the 10 MB size limit.', 413));
  }

  const body = new FormData();
  body.append('file', file);
  return apiRequest<Treatment>(
    `/admin/patients/${patientId}/treatments/${treatmentId}/recommendation-letter`,
    {
      method: 'POST',
      body,
      token: accessToken,
    },
  ).then(normalizeTreatment);
}

export function deleteTreatmentRecommendationLetter(
  patientId: string,
  treatmentId: string,
  accessToken: string,
): Promise<void> {
  return apiRequest<void>(
    `/admin/patients/${patientId}/treatments/${treatmentId}/recommendation-letter`,
    {
      method: 'DELETE',
      token: accessToken,
    },
  );
}

export function fetchRecommendationLetterBlob(
  patientId: string,
  treatmentId: string,
  accessToken: string,
): Promise<Blob> {
  return fetchAuthenticatedBlob(
    `/admin/patients/${patientId}/treatments/${treatmentId}/recommendation-letter`,
    accessToken,
  );
}

export function uploadPatientToothSpacingMap(
  patientId: string,
  file: File,
  accessToken: string,
): Promise<Patient> {
  const validationError = validateToothSpacingMapImage(file);
  if (validationError === 'type') {
    return Promise.reject(
      new ApiError('Unsupported image type. Please upload JPEG, PNG, or WebP.', 415),
    );
  }
  if (validationError === 'size') {
    return Promise.reject(new ApiError('Image file exceeds the 10 MB size limit.', 413));
  }

  const body = new FormData();
  body.append('file', file);
  return apiRequest<Patient>(`/admin/patients/${patientId}/tooth-spacing-map`, {
    method: 'POST',
    body,
    token: accessToken,
  });
}

export function deletePatientToothSpacingMap(
  patientId: string,
  accessToken: string,
): Promise<void> {
  return apiRequest<void>(`/admin/patients/${patientId}/tooth-spacing-map`, {
    method: 'DELETE',
    token: accessToken,
  });
}

export function fetchPatientToothSpacingMapBlob(
  patientId: string,
  accessToken: string,
): Promise<Blob> {
  return fetchAuthenticatedBlob(
    `/admin/patients/${patientId}/tooth-spacing-map`,
    accessToken,
  );
}

export function updateTreatmentStageStep(
  patientId: string,
  treatmentId: string,
  stageId: string,
  stepId: string,
  payload: UpdateTreatmentStageStepPayload,
  accessToken: string,
): Promise<TreatmentStageStep> {
  return apiRequest<TreatmentStageStep>(
    `/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${stageId}/stages/${stepId}`,
    {
      method: 'PATCH',
      body: payload,
      token: accessToken,
    },
  );
}

export const createTreatmentCycle = createTreatmentStage;
export const updateTreatmentCycle = updateTreatmentStage;
export const updateTreatmentCycleStage = updateTreatmentStageStep;
